import { ThemeProvider } from '@emotion/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Modal } from '@mui/material';
import Grid from '@mui/material/Grid';
import { createTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getUserData, loginUser } from 'APIs/AuthApi';
import { postUserData } from 'APIs/UserDataApi';
import { db } from 'app/firebase';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosResponse } from 'axios';
import ImageButton from 'components/atoms/Button/ImageButton';
import SignButton from 'components/atoms/Button/SignButton';
import InputText from 'components/molecules/InputText/InputText';
import { ACCESS_TOKEN } from 'constant';
import { login, selectAuth } from 'features/auth/authSlice';
import { doc, setDoc } from 'firebase/firestore';
import { UserFormData, UserPlatform } from 'interfaces/UserData.interface';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const MainModal = styled(Modal)(({ theme }) => ({
  height: '95vh',
  maxWidth: '425px',
  margin: 'auto',
  overflowY: 'auto',
  borderRadius: '50px',
  boxShadow: theme.shadows[10],
}));

const Body = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingBottom: '50px',
  width: '100%',
}));

const ContentGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  paddingLeft: '57px',
  paddingRight: '57px',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontSize: '25px',
  fontWeight: 'bold',
  paddingBottom: '26px',
}));
const ParaText = styled(TitleText)(({ theme }) => ({
  paddingBottom: '40px',
  fontSize: '16px',
  color: '#c1c1c1',
  fontWeight: 'normal',
}));

const SignUp = () => {
  const authState = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();

  const illustration = require('assets/illustration.png');

  const navigate = useNavigate();

  const emptyFunc = () => {};

  const [usernameAvailable, setUsernameAvailable] = React.useState<
    boolean | null
  >(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const [formData, setFormData] = React.useState<UserFormData>({
    name: '',
    username: '',
    password: '',
    email: '',
    address: '',
    walletAddress: authState.walletAddress || '',
    platform: UserPlatform.CLIENT,
    seenOnBoarding: false,
  });
  const [error, setError] = React.useState<string>('');

  const handleInputChange = (e: any) => {
    const { name, value } = e.currentTarget as HTMLInputElement;
    setFormData({ ...formData, [name]: value });
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    const form = event.currentTarget as HTMLFormElement;
    if (form.username.value !== form.username1.value) {
      setError('Username is not matching');
      return;
    }

    setLoading(true);

    const data: UserFormData = {
      name: formData.name,
      username: formData.username,
      password: 'accessAll',
      email: formData.email,
      walletAddress: formData.walletAddress,
      platform: formData.platform,
    };

    setUsernameAvailable(null);
    try {
      const res: AxiosResponse<any, any> = await postUserData(data,  window.location.origin,);
      if (res.status === 201) {
        const loginRes = await loginUser({
          username: data.username,
          password: data.password,
        });

        if (!(loginRes && loginRes.access_token)) return;

        localStorage.setItem(ACCESS_TOKEN, loginRes.access_token);

        const userData = await getUserData();

        if (userData) {
          dispatch(login({ ...userData }));
          await setDoc(doc(db, 'chats', `${userData.id}`), {});
        }
      }
      // handleResponse && handleResponse(res);
    } catch (error: any) {
      console.log(error);
      if (error.response.status === 403 && error.response.data?.error) {
        setError(error.response.data?.error);
        if (error.response.data?.error === 'User already exists') {
          setUsernameAvailable(false);
        } else {
          setUsernameAvailable(true);
        }
      }
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (!authState.walletAddress) {
      navigate('/signin');
    }
  }, [authState, navigate]);

  return (
    <React.Fragment>
      <ThemeProvider theme={colortheme}>
        <MainModal open={true} className="scrollbar-hidden">
          <Body>
            <ContentGrid sx={{ justifyContent: 'center', padding: 'unset' }}>
              <img
                src={illustration}
                alt={'img'}
                style={{ width: '100%', height: '229px' }}
              />
            </ContentGrid>
            <ContentGrid sx={{ paddingTop: '50px' }}>
              <TitleText>Sign Up</TitleText>
            </ContentGrid>
            <ContentGrid>
              <ParaText>Welcome to Buku!</ParaText>
            </ContentGrid>
            <form onSubmit={handleFormSubmit}>
              {error && (
                <ParaText
                  style={{
                    color: 'red',
                    textAlign: 'center',
                    paddingBottom: '10px',
                  }}
                >
                  {error}
                </ParaText>
              )}

              <ContentGrid style={{ paddingBottom: '20px' }}>
                <InputText
                  initValue=""
                  placeholder={'Full Name'}
                  type={'user'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                  name={'name'}
                  onChange={e => handleInputChange(e)}
                />
              </ContentGrid>
              <ContentGrid style={{ paddingBottom: '30px' }}>
                <InputText
                  initValue=""
                  placeholder={'Email address'}
                  type={'mail'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                  name={'email'}
                  onChange={e => handleInputChange(e)}
                />
              </ContentGrid>
              <ContentGrid style={{ paddingBottom: '20px' }}>
                <InputText
                  initValue=""
                  placeholder={'Username'}
                  type={'user'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                  name={'username'}
                  onChange={e => handleInputChange(e)}
                />

                {usernameAvailable ? (
                  <Box
                    style={{
                      position: 'absolute',
                      display: 'flex',
                      right: '60px',
                      color: '#3fbf7f',
                      fontSize: '14px',
                    }}
                  >
                    <CheckIcon />
                    <Typography>available</Typography>
                  </Box>
                ) : usernameAvailable === false ? (
                  <Box
                    style={{
                      position: 'absolute',
                      display: 'flex',
                      right: '60px',
                      color: '#bf3f3f',
                      fontSize: '14px',
                    }}
                  >
                    <CloseIcon />
                    <Typography>already taken</Typography>
                  </Box>
                ) : (
                  <></>
                )}
              </ContentGrid>
              <ContentGrid style={{ paddingBottom: '20px' }}>
                <Typography
                  component={'span'}
                  sx={{ color: '#ffffff', fontsize: '16px' }}
                >
                  This will be displayed as{' '}
                  <span style={{ color: '#36899f' }}>
                    buku.social/{formData.username}
                  </span>
                </Typography>
              </ContentGrid>
              <ContentGrid style={{ paddingBottom: '20px' }}>
                <InputText
                  onChange={emptyFunc}
                  initValue=""
                  placeholder={'Confirm Username'}
                  type={'user'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                  name={'username1'}
                  // onChange={(e)=>handleInputChange(e)}
                />
              </ContentGrid>
              <ContentGrid style={{ paddingBottom: '20px' }}>
                <InputText
                  initValue={authState.walletAddress || ''}
                  onChange={emptyFunc}
                  placeholder={'Wallet Address'}
                  type={'address'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                  disabled
                />
              </ContentGrid>
              <ContentGrid sx={{ alignSelf: 'self-end' }}>
                <Typography
                  component={'span'}
                  sx={{ color: '#DDB930', paddingBottom: '47px' }}
                >
                  <span>Fogot password?</span>
                </Typography>
              </ContentGrid>
              <ContentGrid
                sx={{
                  justifyContent: 'center',
                  paddingBottom: '45px',
                  paddingX: '80px',
                }}
              >
                <div style={{ width: '100%' }}>
                  <SignButton
                    text={'Sign Up'}
                    color={'yellow'}
                    type="submit"
                    disabled={loading}
                  />
                </div>
              </ContentGrid>
            </form>
            <ContentGrid sx={{ justifyContent: 'center' }}>
              <ParaText style={{ paddingBottom: '20px' }}>
                Or sign up with
              </ParaText>
            </ContentGrid>
            <ContentGrid
              sx={{
                justifyContent: 'center',
                paddingBottom: '58px',
                gap: '5px',
                height: '110px',
              }}
            >
              <ImageButton type={'google'} />
              <ImageButton type={'twitter'} />
            </ContentGrid>
            <ContentGrid sx={{ justifyContent: 'center' }}>
              <Typography component={'span'} sx={{ color: '#c1c1c1' }}>
                Already have an account yet?
                <span
                  style={{ color: '#DDB930' }}
                  onClick={() => navigate('/signin')}
                >
                  &nbsp;&nbsp; Sign In
                </span>
              </Typography>
            </ContentGrid>
          </Body>
        </MainModal>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default SignUp;
